import { ChangeEvent } from 'react'
import { Form, useFormikContext } from 'formik'
import { useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { StepFormMobile } from './step-form-mobile'
import { StepFormDesktop } from './step-form-desktop'

import { useMenuItems, usePrompt } from 'src/hooks'
import { IStepForm, ETranslationNs } from 'src/types'
import { COORDS_MULTIPLIER } from 'src/constants'
import { LocationsMapContext } from 'src/components/locaions/context/map.context'

interface IStepFormProps {
  isAddingNew?: boolean
}

interface IStepFormP extends IStepFormProps {
  isLoading: boolean
  locale?: string
  handleLocale?: (locale: string) => void
  disabled?: boolean
}

const StepForm = ({ isLoading, isAddingNew, locale, handleLocale, disabled }: IStepFormP) => {
  const [t] = useTranslation()
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))
  const { setFieldValue, dirty, isSubmitting } = useFormikContext<IStepForm>()
  usePrompt({
    message: isLoading ? t('prompt-loading-changes.v') : t('prompt-unsaved-changes.v'),
    when: (dirty && !isSubmitting) || isLoading,
    beforeUnload: true,
  })

  const { handleChooseMenuItem, menuItems } = useMenuItems([
    { title: t('step-header-title.v', { ns: ETranslationNs.Navbar }), isActive: true },
  ])

  const handleLocationNameChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) =>
    setFieldValue(`locations[${index}].location_name`, e.target.value)

  const handleAddressChange = (index: number) => (val: string) =>
    setFieldValue(`locations[${index}].location_address`, val)

  const handleLatitudeAndLongitudeChange =
    (index: number) => (center: { lat: number; lng: number }) => {
      setFieldValue(`locations[${index}].latitude`, Math.round(center.lat * COORDS_MULTIPLIER))
      setFieldValue(`locations[${index}].longitude`, Math.round(center.lng * COORDS_MULTIPLIER))
    }

  return (
    <Form>
      <LocationsMapContext.Provider
        value={{
          handleAddress: handleAddressChange,
          handleChangeRadius: (index: number) => (radius: number) =>
            setFieldValue(`locations[${index}].radius`, radius),
          handleLatitudeAndLongitude: handleLatitudeAndLongitudeChange,
          handleLocationName: handleLocationNameChange,
        }}
      >
        {isTablet ? (
          <StepFormMobile
            isAddingNew={isAddingNew}
            isDisabled={disabled}
            locale={locale}
            handleLocale={handleLocale}
          />
        ) : (
          <StepFormDesktop
            isDisabled={disabled}
            locale={locale}
            handleLocale={handleLocale}
            menuItems={menuItems}
            handleChooseMenuItem={handleChooseMenuItem}
            isAddingNew={isAddingNew}
          />
        )}
      </LocationsMapContext.Provider>
    </Form>
  )
}

export { type IStepFormProps, StepForm }
